import { FC, FormEvent, ReactElement, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { KeyboardBackspace } from "@mui/icons-material";

import { TextInput } from "@/components";
import { useSnackbar } from "@/hooks";
import { forgot_password_icon } from "@/constants";
import { useForgetPasswordMutation } from "@/services";
import { isValidEmail } from "@/utils/form-validation";

const ForgotPassword: FC = (): ReactElement => {
  const { show } = useSnackbar();
  const navigate = useNavigate();
  const [forgetPassword] = useForgetPasswordMutation();
  const [email, setEmail] = useState<string>("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValidEmail(email)) {
      show("Invalid email address!", "Please enter a valid email.", "error");
      return;
    }
    show(
      "Reset Instruction sent to your email!",
      "We’ve sent you a link where you can change your password.",
      "info"
    );
    forgetPassword({ email })
      .unwrap()
      .catch((error) => {
        show("Invalid email!", error.message, "error");
      });
    setEmail("");
  };

  return (
    <Stack
      direction={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      height={"100%"}
    >
      <Box sx={{ width: { xs: "95%", sm: "70%" }, p: { xs: 2, md: 0 } }}>
        <form onSubmit={handleSubmit}>
          <Stack
            spacing={1}
            direction={"column"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <img src={forgot_password_icon} alt="forgot password icon" width={74} />
            <Box textAlign={"center"}>
              <Typography
                fontSize={{ md: "32px", xs: "22px" }}
                fontWeight={600}
              >
                Forgot your password?
              </Typography>
              <Typography fontSize={14} color={"neutral.500"}>
                Enter your email address below and we'll send you password reset
                instructions.
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ my: 2 }}>
            <Stack spacing={2}>
              <Box>
                <TextInput
                  label="Email"
                  type="email"
                  placeholder="hi@example.com"
                  color="success"
                  error={false}
                  helperText={""}
                  value={email}
                  handleChangeValue={(val: string) => setEmail(val)}
                />
              </Box>
            </Stack>
          </Box>
          <Button
            variant="contained"
            fullWidth
            sx={{
              mt: 2,
              py: 1.5,
              fontSize: "16px",
              fontWeight: 500,
            }}
            disabled={!isValidEmail(email)}
            type="submit"
            color="primary"
          >
            Send Reset Instructions
          </Button>

          <Button
            variant="outlined"
            fullWidth
            sx={{
              mt: 2,
              py: 1.5,
              fontSize: "16px",
              fontWeight: 500,
              color: "#030712",
              border: "1px solid #D4D6D9",
            }}
            color="secondary"
            onClick={() => navigate(-1)}
            startIcon={<KeyboardBackspace />}
          >
            Go Back
          </Button>
          <Divider sx={{ my: 4 }} />
          <Typography fontSize={14} color={"neutral.500"}>
            If you don't see your reset email be sure to check your spam filter
            for an email from{" "}
            <strong style={{ color: "#0B494B" }}>support@riffs.com</strong>
          </Typography>
        </form>
      </Box>
    </Stack>
  );
};

export default ForgotPassword;
